"use client";

import { useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { ChevronRight, Mail } from "lucide-react";
import { entraLoginRedirect } from "@/lib/auth/entra/browser-actions";
import { rememberPostAuthOrigin } from "@/lib/auth/entra/redirect-uri";

/**
 * B2.10 Phase A — Savira is invite-only. The Microsoft button is the sign-in
 * path for users who already received an invitation (or who have a Microsoft
 * work/school account that was previously redeemed). In "signup" mode we
 * show a non-actionable message directing prospects to request access.
 */
export function EntraSignInPanel({ mode }: { mode: "signin" | "signup" }) {
    const searchParams = useSearchParams();
    const invited = searchParams.get("invited") === "1";
    const entraRedirect = searchParams.get("entraRedirect") === "1";
    const returnOrigin = searchParams.get("returnOrigin");
    const autoSignInStarted = useRef(false);

    const handleClick = async () => {
        await entraLoginRedirect();
    };

    useEffect(() => {
        if (mode !== "signin" || (!invited && !entraRedirect) || autoSignInStarted.current) return;
        if (
            typeof window !== "undefined" &&
            (window as unknown as { __SAVIRA_E2E_NO_ENTRA_REDIRECT__?: boolean })
                .__SAVIRA_E2E_NO_ENTRA_REDIRECT__
        ) {
            return;
        }
        autoSignInStarted.current = true;
        if (entraRedirect && returnOrigin) {
            rememberPostAuthOrigin(returnOrigin);
        }
        void entraLoginRedirect();
    }, [mode, invited, entraRedirect, returnOrigin]);

    if (mode === "signup") {
        return (
            <div className="space-y-5">
                <div>
                    <h2 className="mb-2 text-[17px] font-extrabold tracking-[-0.01em] text-ink">
                        Savira is invite-only
                    </h2>
                    <p className="max-w-[58ch] text-sm text-ink-2">
                        New accounts are created by a Savira teammate or by your
                        organization&apos;s admin. If you&apos;ve been invited, check
                        your inbox for a Microsoft &ldquo;Set up your account&rdquo;
                        email and click the link.
                    </p>
                </div>

                <a
                    href="mailto:sales@savira.io?subject=Savira%20access%20request"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-line-2 bg-card px-5 py-3 text-[15px] font-bold leading-tight text-ink transition-colors hover:border-ink-3 sm:w-auto"
                >
                    <Mail className="w-4 h-4" />
                    Request access
                </a>

                <p className="text-[13px] font-semibold text-ink-3">
                    Already have an invite link? Open it directly &mdash; you&apos;ll
                    land here after redeeming.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <div>
                <h2 className="mb-2 text-[17px] font-extrabold tracking-[-0.01em] text-ink">Welcome back</h2>
                <p className="max-w-[58ch] text-sm text-ink-2">
                    {invited || entraRedirect
                        ? "Your account is ready. Opening sign-in…"
                        : "Sign in with the account you used when you redeemed your Savira invitation."}
                </p>
            </div>

            <button
                type="button"
                onClick={() => void handleClick()}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-accent bg-accent px-5 py-3 text-[15px] font-bold leading-tight text-accent-ink transition-[filter] hover:brightness-[1.06] sm:inline-flex sm:w-auto"
            >
                Sign-in
                <ChevronRight className="w-5 h-5" />
            </button>

            <p className="text-[13px] font-semibold text-ink-3">
                Don&apos;t have an account yet?{" "}
                <a
                    href="mailto:sales@savira.io?subject=Savira%20access%20request"
                    className="font-bold text-accent hover:underline"
                >
                    Request access
                </a>
            </p>
        </div>
    );
}
